import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { LoadingFailedBanner, LoadingFailedBox, NgPageHeader, useDebugMode } from '@/ui';
import { IncidentListMap } from '@/features/shared/map/IncidentListMap';
import { toRow } from '@ngsos/portal-core/incidents';

import type { IncidentArchiveFilters } from './filters';
import { applyFiltersToSearch, buildGetIncidentsRequest, parseFiltersFromSearch } from './filters';
import { getIncidents } from './incidentsApi';
import { IncidentArchiveActionBox } from './IncidentArchiveActionBox';
import { IncidentArchiveLayoutBox } from './IncidentArchiveLayoutBox';
import { IncidentArchivePageList } from './IncidentArchivePageList';

export function IncidentArchivePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const debugModeOn = useDebugMode();

  const filters = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);

  const body = useMemo(() => buildGetIncidentsRequest(filters), [filters]);

  const {
    data: incidentsFromApi,
    isFetching: isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['incident-archive', body],
    queryFn: () => getIncidents(body),
  });

  const incidents = useMemo(
    () =>
      incidentsFromApi != null
        ? incidentsFromApi
            .filter((incident) => debugModeOn || incident.purpose !== 'Test')
            .sort(
              (a, b) =>
                new Date(b.createdTimeUtc ?? 0).getTime() - new Date(a.createdTimeUtc ?? 0).getTime(),
            )
        : null,
    [debugModeOn, incidentsFromApi],
  );

  const retryLoad = async () => {
    await refetch();
  };

  const applyFilters = (filtersFromComponent: IncidentArchiveFilters) => {
    navigate({
      pathname: location.pathname,
      search: applyFiltersToSearch(location.search, filtersFromComponent),
    });
  };

  return (
    <>
      <NgPageHeader title={t('IncidentArchive_PageTitle')} />

      <IncidentArchiveLayoutBox
        actionBarArea={<IncidentArchiveActionBox filtersApplied={applyFilters} filters={filters} />}
        incidentListArea={
          <>
            {isError && incidents != null && <LoadingFailedBanner onRetry={retryLoad} />}

            {isError && incidents == null ? (
              <LoadingFailedBox onRetry={retryLoad} />
            ) : (
              <IncidentArchivePageList incidents={incidents ?? []} isLoading={isLoading} />
            )}
          </>
        }
        mapArea={<IncidentListMap incidents={incidents?.map(toRow) ?? null} />}
      />
    </>
  );
}
